/**
 * Admin Navbar Component
 *
 * Horizontal navigation bar for admin interface with tabs:
 * - ダッシュボード (Dashboard)
 * - 従業員 (Employees)
 * - 招待 (Invitations)
 * - 提出確認 (Submission review)
 * - 管理者 (Admins)
 * - 設定 (Settings)
 *
 * This navbar appears below the header and above the main content area.
 * Active tab is highlighted with a light green/beige background.
 */
import React from 'react';
import { useSecretExpiry } from '../../../../contexts/SecretExpiryContext';
import { useAdminChatUnread } from '../../../../contexts/AdminChatUnreadContext';
import './navbar.css';

export type NavTab = 'dashboard' | 'employees' | 'invitations' | 'documents' | 'messages' | 'admins' | 'settings';

export interface AdminNavbarProps {
  /** Currently active tab */
  activeTab?: NavTab;
  /** Callback when a tab is clicked */
  onTabChange?: (tab: NavTab) => void;
}

const AdminNavbar: React.FC<AdminNavbarProps> = ({
  activeTab = 'dashboard',
  onTabChange
}) => {
  const secretExpiry = useSecretExpiry();
  const chatUnread = useAdminChatUnread();

  const handleTabClick = (tab: NavTab) => {
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const tabs: { id: NavTab; label: string }[] = [
    { id: 'dashboard', label: 'ダッシュボード' },
    { id: 'employees', label: '従業員' },
    { id: 'invitations', label: '招待' },
    { id: 'documents', label: '提出確認' },
    { id: 'messages', label: 'メッセージ' },
    { id: 'admins', label: '管理者' },
    { id: 'settings', label: '設定' },
  ];

  return (
    <nav className="admin-navbar" aria-label="Admin navigation">
      <div className="admin-navbar__container">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-navbar__tab ${
              activeTab === tab.id ? 'admin-navbar__tab--active' : ''
            } ${tab.id === 'settings' && secretExpiry.warning ? `admin-navbar__tab--dot admin-navbar__tab--dot-${secretExpiry.severity}` : ''}${
              tab.id === 'messages' && chatUnread.count > 0 ? ' admin-navbar__tab--dot admin-navbar__tab--dot-urgent' : ''
            }`}
            onClick={() => handleTabClick(tab.id)}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            {tab.label}
            {tab.id === 'settings' && secretExpiry.warning && (
              <span className="admin-navbar__dot" aria-hidden />
            )}
            {tab.id === 'messages' && chatUnread.count > 0 && (
              <span className="admin-navbar__dot" aria-hidden />
            )}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default AdminNavbar;
